import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { House, ArrowLeft, ArrowRight, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";
import PhotoUpload from "@/components/PhotoUpload";

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ["Formule", "Adresse", "Créneau", "Coordonnées", "Récapitulatif"];

export default function ApartmentCleaningPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState(null);
  const [formula, setFormula] = useState("altura_materiel");
  const [hours, setHours] = useState(3);
  const [address, setAddress] = useState("");
  const [addressDetails, setAddressDetails] = useState("");
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [desiredDate, setDesiredDate] = useState("");
  const [desiredTime, setDesiredTime] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientSurname, setClientSurname] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [comments, setComments] = useState("");
  const [priceResult, setPriceResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [whatsappLink, setWhatsappLink] = useState("");

  useEffect(() => { axios.get(`${API}/api/property/catalog`).then(({ data }) => setCatalog(data)).catch(() => {}); }, []);

  const calcDist = useCallback(async () => {
    if (address.length < 5) return;
    try { const { data } = await axios.get(`${API}/api/property/distance`, { params: { address } }); setDistanceInfo(data); } catch { setDistanceInfo(null); }
  }, [address]);

  const calcPrice = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/property/calculate-price`, {
        service_type: "appartement", client_name: clientName || "P", client_email: clientEmail || "p@t.com", client_phone: clientPhone || "+33",
        address, apartment_formula: formula, apartment_hours: hours, distance_km: distanceInfo?.distance_km || null,
      });
      setPriceResult(data);
    } catch { toast.error("Erreur calcul"); } finally { setLoading(false); }
  }, [formula, hours, address, distanceInfo, clientName, clientEmail, clientPhone]);

  useEffect(() => { if (step === 4) calcPrice(); }, [step, calcPrice]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/property/request`, {
        service_type: "appartement", client_name: clientName, client_surname: clientSurname, client_email: clientEmail, client_phone: clientPhone,
        address, address_details: addressDetails, desired_date: desiredDate, desired_time: desiredTime,
        apartment_formula: formula, apartment_hours: hours, distance_km: distanceInfo?.distance_km || null, comments, photo_urls: photos.map(p => p.url),
      });
      if (data.success) { setWhatsappLink(data.whatsapp_link || ""); setSubmitted(true); toast.success("Demande envoyée !"); }
    } catch { toast.error("Erreur"); } finally { setSubmitting(false); }
  };

  const canNext = () => {
    if (step === 0) return formula;
    if (step === 1) return address.length >= 5;
    if (step === 3) return clientName && clientEmail && clientPhone;
    return true;
  };

  const liveTotal = catalog ? (catalog.appartement.formulas[formula]?.hourly_rate || 0) * Math.max(2, hours) : 0;

  if (submitted) return (
    <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="apartment-success">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Demande envoyée</h1>
        <p className="text-[#1A1A1A]/60 text-sm mb-6">Notre équipe confirmera votre créneau de ménage très rapidement.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate("/property-services")} className="bg-[#1A1A1A] text-white px-6 py-3 text-xs tracking-[0.15em] uppercase">Retour</button>
          {whatsappLink && <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="border border-[#1A1A1A]/20 text-[#1A1A1A] px-6 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#1A1A1A]/50" data-testid="whatsapp-link">Nous contacter sur WhatsApp</a>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO title="Nettoyage Appartement — ALTURA Property Services" description="Ménage professionnel à l'heure. Matériel fourni ou non." />
      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => navigate("/property-services")} className="text-white/50 text-xs flex items-center gap-2 mb-6 hover:text-white"><ArrowLeft size={12} /> Property Services</button>
          <h1 className="font-heading text-2xl sm:text-3xl text-white tracking-wide" data-testid="apartment-title">Nettoyage Appartement</h1>
          <div className="flex gap-1 mt-6">{STEPS.map((l, i) => (<div key={l} className="flex-1"><div className={`h-1 ${i <= step ? "bg-white" : "bg-white/20"}`} /><p className={`text-[10px] mt-1 ${i <= step ? "text-white/80" : "text-white/30"}`}>{l}</p></div>))}</div>
        </div>
      </section>
      <section className="bg-[#EDEBE4] py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-6">
          {step === 0 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-formula">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Choisissez votre formule</h2>
              <div className="space-y-3 mb-6">
                {catalog && Object.entries(catalog.appartement.formulas).map(([k, f]) => (
                  <button key={k} onClick={() => setFormula(k)}
                    className={`w-full text-left p-5 border transition-colors ${formula === k ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white border-[#1A1A1A]/20 hover:border-[#1A1A1A]/50"}`}
                    data-testid={`formula-${k}`}>
                    <div className="flex justify-between items-center">
                      <div><p className="text-sm font-medium">{f.name}</p></div>
                      <p className="text-sm font-medium ml-4">{f.hourly_rate} €/h</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-5">
                <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-3">Nombre d'heures (minimum 2h)</label>
                <div className="flex items-center gap-4">
                  <input type="range" min="2" max="10" value={hours} onChange={e => setHours(parseInt(e.target.value))}
                    className="flex-1 accent-[#1A1A1A]" data-testid="hours-slider" />
                  <span className="text-[#1A1A1A] font-heading text-xl w-12 text-center">{hours}h</span>
                </div>
                <p className="text-[#1A1A1A] text-sm mt-3">Tarif estimé : <strong>{liveTotal} €</strong></p>
              </div>
            </motion.div>
          )}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-address">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Adresse d'intervention</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Adresse complète</label><input type="text" value={address} onChange={e => setAddress(e.target.value)} onBlur={calcDist} placeholder="123 Rue..." className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none" data-testid="address-input" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Précisions</label><input type="text" value={addressDetails} onChange={e => setAddressDetails(e.target.value)} placeholder="Code, étage..." className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
                {distanceInfo && distanceInfo.distance_km > 20 && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4"><p className="text-yellow-700 text-sm">Votre adresse est à {distanceInfo.distance_km} km de notre base (au-delà du rayon de 20 km). Des frais de déplacement pourront s'appliquer — notre équipe vous recontactera.</p></div>
                )}
                {distanceInfo && distanceInfo.distance_km <= 20 && (
                  <div className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-4"><p className="text-[#1A1A1A]/70 text-sm">{distanceInfo.distance_km} km — dans le rayon de 20 km, pas de frais de déplacement.</p></div>
                )}
              </div>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-schedule">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Créneau souhaité</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Date</label><input type="date" value={desiredDate} onChange={e => setDesiredDate(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Heure</label><input type="time" value={desiredTime} onChange={e => setDesiredTime(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
              </div>
            </motion.div>
          )}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-contact">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Vos coordonnées</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4"><div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Prénom *</label><input type="text" value={clientName} onChange={e => setClientName(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div><div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Nom</label><input type="text" value={clientSurname} onChange={e => setClientSurname(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Email *</label><input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Téléphone *</label><input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Commentaires</label><textarea value={comments} onChange={e => setComments(e.target.value)} rows={3} placeholder="Demandes particulières..." className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none resize-none" /></div>
                <PhotoUpload photos={photos} setPhotos={setPhotos} hint="Photos de l'appartement pour mieux préparer notre intervention." />
              </div>
            </motion.div>
          )}
          {step === 4 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-summary">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Récapitulatif</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div><p className="text-[#1A1A1A]/50 text-xs uppercase mb-1">Client</p><p className="text-sm">{clientName} {clientSurname} — {clientPhone}</p></div>
                <div><p className="text-[#1A1A1A]/50 text-xs uppercase mb-1">Adresse</p><p className="text-sm">{address}</p></div>
                {desiredDate && <div><p className="text-[#1A1A1A]/50 text-xs uppercase mb-1">Créneau</p><p className="text-sm">{desiredDate} {desiredTime && `à ${desiredTime}`}</p></div>}
                <hr className="border-[#D0CEC7]" />
                {loading ? <p className="text-sm text-[#1A1A1A]/50"><Spinner size={14} className="inline animate-spin mr-1" />Calcul...</p> : priceResult && (
                  <>
                    {priceResult.breakdown.map((b, i) => <div key={i} className="flex justify-between text-sm"><span>{b.label}</span><span className="font-medium">{b.amount > 0 ? `${b.amount} €` : "Sur devis"}</span></div>)}
                    <hr className="border-[#D0CEC7]" />
                    <div className="flex justify-between"><span className="font-heading text-lg">TOTAL</span><span className="font-heading text-lg" data-testid="total-amount">{priceResult.total} €</span></div>
                  </>
                )}
              </div>
            </motion.div>
          )}
          <div className="flex justify-between mt-8">
            {step > 0 ? <button onClick={() => setStep(s => s - 1)} className="text-[#1A1A1A]/60 text-xs flex items-center gap-2 uppercase tracking-wide"><ArrowLeft size={12} /> Précédent</button> : <div />}
            {step < 4 ? <button onClick={() => canNext() && setStep(s => s + 1)} disabled={!canNext()} className={`flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-wide ${canNext() ? "bg-[#1A1A1A] text-white" : "bg-[#1A1A1A]/20 text-[#1A1A1A]/40 cursor-not-allowed"}`} data-testid="next-btn">Suivant <ArrowRight size={12} /></button>
            : <button onClick={handleSubmit} disabled={submitting} className="bg-[#1A1A1A] text-white px-8 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-50" data-testid="submit-btn">{submitting ? "Envoi..." : "Envoyer ma demande"}</button>}
          </div>
        </div>
      </section>
    </>
  );
}
